import React, {ReactNode, createContext, useCallback, useEffect, useRef, useState} from "react";
import {useSessionCookie} from "../hooks/useSessionCookie";
import {useFeedback} from "../hooks/useFeedback";
import {useAsync} from "../hooks/useAsyncResult";
import {getNotifications, markAsRead} from "../services/api/notification";
import {User} from "../models/user";
import {registerMessagingToken} from "../services/api/messaging";
import {Notification as INotification} from "../models/notification";
import {messaging} from "../firebase";
import {getToken, onMessage} from "firebase/messaging";

export type NotificationContextState = {
    token?: string,
    notifications?: { data: INotification[], count: number },
    notificationsEnabled?: boolean,
    enableNotifications?: () => void,
    loadNotifications?: (page?: number) => void,
    loading?: boolean,
    badgeCount?: number,
    setBadgeCount?: (count: number) => void,
    markedAsRead?: string[],
    markAsRead?: (notificationId: string) => void,
    markAllAsRead?: () => void,
}

export const INotificationContext = createContext<NotificationContextState>({})

const VAPID_KEY = process.env.REACT_APP_FIREBASE_VAPID_KEY

const NotificationContext = (props: { children: ReactNode }) => {
    const {userSession} = useSessionCookie()
    const markAsReadAsyncResult = useAsync(markAsRead, true)
    const [markedAsRead, setMarkedAsRead] = useState<string[]>([])
    const [badgeCount, setBadgeCount] = useState<number>(0)
    const tokenRegistered = useRef(false)
    const feedback = useFeedback()
    const notificationsAsyncResult = useAsync(getNotifications, true)
    const user: User = userSession?.['currentUser']
    // Load only once the app session exists — see SubscriptionContext.
    const sessionToken = userSession?.token
    const [token, setToken] = useState<string | null>(null)
    const [notificationsEnabled, setNotificationsEnabled] = useState(
        typeof Notification !== "undefined" && Notification.permission === "granted"
    )
    const registerMessagingTokenAsyncResult = useAsync(registerMessagingToken, true)

    const parseFCMData = (data: Record<string, string>): INotification => {
        return {
            action: JSON.parse(data['action']),
            body: data['body'],
            date: JSON.parse(data['date']),
            title: data['title'],
            receiverId: data['receiverId'],
            pending: JSON.parse(data['pending'])
        }
    }

    const handleMarkAsRead = (notificationId: string) => {
        if (!notificationId || markedAsRead.includes(notificationId)) return
        markAsReadAsyncResult.execute(notificationId)
        setBadgeCount(prevState => Math.max(0, prevState - 1))
        setMarkedAsRead(prevState => ([...prevState, notificationId]))
    }

    const registerToken = useCallback(async () => {
        try {
            if (tokenRegistered.current || !messaging)
                return

            tokenRegistered.current = true
            const registration = await navigator.serviceWorker.register("/firebase-messaging-sw.js")
            const currentToken = await getToken(messaging, {
                vapidKey: VAPID_KEY,
                serviceWorkerRegistration: registration,
            })
            if (currentToken) {
                setToken(currentToken)
                await registerMessagingTokenAsyncResult.execute(currentToken)
            }
        } catch (e) {
            tokenRegistered.current = false
            console.error(e)
        }
    }, [registerMessagingTokenAsyncResult])


    useEffect(() => {
        if (!user?.id || (typeof Notification !== "undefined" && Notification.permission !== "granted"))
            return

        registerToken()
    }, [user?.id])

    useEffect(() => {
        if (sessionToken)
            notificationsAsyncResult.execute()
        else {
            notificationsAsyncResult.setData(null)
            setBadgeCount(0)
        }
    }, [sessionToken])

    // Every load (not just the first) brings the server's unread count, so the badge follows reloads,
    // page changes and new results.
    useEffect(() => {
        if (notificationsAsyncResult.isSuccess)
            setBadgeCount(notificationsAsyncResult.payload?.data.unread ?? 0)
    }, [notificationsAsyncResult.payload])

    const enableNotifications = useCallback(async () => {
        if (typeof Notification === "undefined") {
            feedback.showFeedback("Push notifications are not supported on this browser", "danger")
            return
        }

        const permission = await Notification?.requestPermission()
        setNotificationsEnabled(permission === "granted")
        if (permission !== "granted") return
        await registerToken()

    }, [registerToken])

    useEffect(() => {
        if (!user?.id || !messaging) return

        const unsubscribe = onMessage(messaging, (payload) => {
            console.log("Foreground message:", payload)
            feedback.showResultsFeedback(parseFCMData(payload.data))
            // The new notification is saved server-side before it's pushed; reload to pick it up.
            notificationsAsyncResult.execute()
        })
        return () => unsubscribe()
    }, [user?.id])


    return <INotificationContext.Provider value={{
        token,
        notificationsEnabled,
        enableNotifications,
        loadNotifications: notificationsAsyncResult.execute,
        notifications: {
            data: notificationsAsyncResult.payload?.data.notifications,
            count: notificationsAsyncResult.payload?.count
        },
        loading: notificationsAsyncResult.loading,
        markedAsRead,
        markAsRead: handleMarkAsRead,
        badgeCount,
        setBadgeCount
    }}>
        {props.children}
    </INotificationContext.Provider>

}

export default NotificationContext